import { APIError, apiRequest } from "@core/services/api/apiRequest";

import type { QuizQuestion } from "@core/features/quiz";
import type { IntakeFieldName } from "./MarketingIntakeForm";

/** How a configured form describes itself to whoever is about to fill it in. */
export type IntakeFormConfig = {
  id: number;
  slug: string;
  name: string;
  /** The quiz whose questions are the ones only this form asks */
  quiz_id: number | null;
  questions: QuizQuestion[];
  required_fields: IntakeFieldName[];
  optional_fields: IntakeFieldName[];
  /** Wording overrides for the built-in fields */
  field_labels: Record<string, string>;
  /** Whether this form collects a time as well as answers */
  books_a_meeting: boolean;
  /** Calendly's own page, offered when a booking cannot be completed here */
  scheduling_url: string;
  /** How bookable times are offered: a day then a time, or a list of sessions */
  booking_step_style: string;
  /** The panel beside the form */
  audience_label: string;
  headline: string;
  intro_body: string[];
  step_intro: string;
  /** The wording on the button that finishes the form */
  submit_label: string;
};

/**
 * The configuration behind one intake form.
 *
 * Defined once because more than one thing on a page wants it: the form asks
 * which fields to render, while the page around it asks what the form is
 * called. Sharing the query means they agree and it is fetched once.
 */
export const intakeFormQuery = (formSlug: string) => ({
  queryKey: ["intakeForm", formSlug],
  queryFn: (): Promise<IntakeFormConfig> =>
    apiRequest(`/intake/${formSlug}/`, "GET", undefined, false),
  // A slug that names no form is a mistyped or retired link, and asking again
  // will not produce one. Retrying leaves a visitor watching a spinner for ten
  // seconds before being told the same thing.
  retry: (failureCount: number, error: Error) =>
    !(error instanceof APIError && error.status === 404) && failureCount < 3,
});
